import { useEffect, useRef, useState } from "react";

interface ShareCard {
  id: string;
  theme: string;
  label: string;
  gradient: string;
  textColor: string;
  tagline: string;
  image: string;
}

const shareCards: ShareCard[] = [
  {
    id: "sea", theme: "山海版", label: "碧海蓝天", gradient: "from-sky-400/80 to-cyan-600/80", textColor: "text-sky-100", tagline: "一城山海，万般美好",
    image: "https://public.readdy.ai/ai/img_res/19087b95cbf82cfd6a93ae8d7d0ef569.jpg",
  },
  {
    id: "night", theme: "夜色版", label: "灯火阑珊", gradient: "from-indigo-900/80 to-slate-900/80", textColor: "text-indigo-100", tagline: "山海厦门，欢迎你来",
    image: "https://readdy.ai/api/search-image?query=Xiamen%20city%20night%20panorama%20from%20high%20above%2C%20glittering%20lights%20reflecting%20on%20dark%20sea%20water%2C%20illuminated%20bridges%20and%20coastal%20roads%2C%20deep%20blue%20night%20sky%20with%20stars%2C%20long%20exposure%20photography%2C%20stunning%20aerial%20cityscape%2C%20ultra%20detailed&width=800&height=1000&seq=ending002&orientation=portrait",
  },
  {
    id: "sunset", theme: "日落版", label: "落霞余晖", gradient: "from-orange-400/80 to-rose-600/80", textColor: "text-orange-100", tagline: "鹭岛之旅，此刻启程",
    image: "https://readdy.ai/api/search-image?query=Xiamen%20Gulangyu%20island%20sunset%20view%2C%20dramatic%20orange%20red%20sky%2C%20silhouette%20of%20colonial%20buildings%20and%20palm%20trees%2C%20calm%20sea%20reflecting%20golden%20light%2C%20romantic%20atmosphere%2C%20cinematic%20photography%2C%20ultra%20detailed&width=800&height=1000&seq=ending003&orientation=portrait",
  },
];

interface EndingPageProps {
  visible: boolean;
  onPrev: () => void;
  onRestart: () => void;
}

export default function EndingPage({ visible, onPrev, onRestart }: EndingPageProps) {
  const [entered, setEntered] = useState(false);
  const [activeCard, setActiveCard] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const autoRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (visible) {
      const t = setTimeout(() => setEntered(true), 80);
      autoRef.current = setInterval(() => {
        setActiveCard((p) => (p + 1) % shareCards.length);
      }, 3000);
      return () => {
        clearTimeout(t);
        if (autoRef.current) clearInterval(autoRef.current);
      };
    } else {
      setEntered(false);
      setSaved(false);
      if (autoRef.current) clearInterval(autoRef.current);
    }
  }, [visible]);

  const handleCardSelect = (i: number) => {
    setActiveCard(i);
    if (autoRef.current) clearInterval(autoRef.current);
  };

  const triggerToast = (msg: string) => {
    setToastMsg(msg);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2500);
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    await new Promise((r) => setTimeout(r, 1200));
    setSaving(false);
    setSaved(true);
    triggerToast("海报已保存到相册！快去分享给朋友吧");
  };

  const handleShare = () => {
    triggerToast("复制链接成功！快分享你的鹭岛之旅");
  };

  const card = shareCards[activeCard];

  return (
    <div className="relative w-full h-screen overflow-hidden bg-gray-950 flex flex-col">
      {/* Background */}
      <div className="absolute inset-0">
        {shareCards.map((c, i) => (
          <div key={c.id} className={`absolute inset-0 transition-opacity duration-1000 ${i === activeCard ? "opacity-100" : "opacity-0"}`}>
            <img src={c.image} alt={c.theme} className="w-full h-full object-cover object-top scale-105" />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-black/30" />
      </div>

      {/* Top Nav */}
      <nav className="relative z-30 flex items-center justify-between px-5 md:px-8 py-4 md:py-5">
        <img src="https://public.readdy.ai/ai/img_res/87bc98b4-e616-459b-81b0-cc0db54579b8.png" alt="Logo" className="h-8 md:h-9 w-auto object-contain" />
        <div className="hidden md:flex items-center gap-6">
          {["山海", "人文", "美食", "活力"].map((item) => (
            <span key={item} className="text-white/50 text-xs tracking-widest cursor-pointer hover:text-white transition-colors whitespace-nowrap">{item}</span>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative z-20 flex-1 flex flex-col md:flex-row items-center px-5 md:px-16 gap-6 md:gap-12 overflow-y-auto pb-4">

        {/* Left: Text Content */}
        <div className={`flex-1 flex flex-col justify-center transition-all duration-800 ${entered ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}>
          <div className="inline-flex items-center gap-2 mb-3 md:mb-6">
            <span className="text-white/40 text-xs tracking-[0.25em] font-mono">07 / FINALE</span>
            <div className="w-6 md:w-8 h-px bg-white/20" />
          </div>

          <h2
            className="text-3xl md:text-7xl font-light text-white leading-tight mb-2 md:mb-4"
            style={{ fontFamily: "'Noto Serif SC', serif", letterSpacing: "0.04em" }}
          >
            山海厦门
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-orange-400">
              欢迎你来
            </span>
          </h2>

          <p className="text-white/60 text-sm md:text-lg font-light tracking-widest mb-1 md:mb-2">{card.tagline}</p>
          <p className="text-white/40 text-xs md:text-sm tracking-wider mb-5 md:mb-10">分享你的鹭岛之旅，让更多人遇见厦门</p>

          {/* Stats row */}
          <div className={`flex items-center gap-5 md:gap-8 mb-5 md:mb-10 transition-all duration-800 delay-200 ${entered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            {[{ num: "7", label: "精彩章节" }, { num: "6", label: "互动体验" }, { num: "∞", label: "美好回忆" }].map((s) => (
              <div key={s.label} className="text-center">
                <div className="text-xl md:text-2xl font-light text-white mb-0.5" style={{ fontFamily: "'Noto Sans SC'" }}>{s.num}</div>
                <div className="text-white/40 text-xs tracking-wider">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className={`flex flex-wrap gap-2 md:gap-3 transition-all duration-800 delay-300 ${entered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <button
              onClick={handleSave}
              disabled={saving}
              className={`flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 rounded-full text-xs md:text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap
                ${saved ? "bg-green-500 text-white" : "bg-white text-gray-900 hover:bg-amber-50"} ${saving ? "opacity-70" : ""}`}
            >
              {saving ? <><i className="ri-loader-4-line animate-spin" />保存中...</> : saved ? <><i className="ri-check-line" />已保存海报</> : <><i className="ri-download-2-line" />保存海报</>}
            </button>
            <button
              onClick={handleShare}
              className="flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 rounded-full border border-white/30 text-white text-xs md:text-sm font-medium hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-share-forward-line" />分享给朋友
            </button>
            <button
              onClick={onRestart}
              className="flex items-center gap-2 px-4 md:px-6 py-2.5 md:py-3 rounded-full border border-white/20 text-white/70 text-xs md:text-sm hover:text-white hover:border-white/40 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line" />再玩一次
            </button>
          </div>
        </div>

        {/* Right: Poster Preview — hidden on small mobile, shown on md+ */}
        <div className={`hidden md:flex flex-col items-center gap-4 transition-all duration-800 delay-150 flex-shrink-0 ${entered ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"}`}>
          <div className="relative w-44 lg:w-52 rounded-3xl overflow-hidden" style={{ height: "280px" }}>
            {shareCards.map((c, i) => (
              <div key={c.id} className={`absolute inset-0 transition-opacity duration-700 ${i === activeCard ? "opacity-100" : "opacity-0"}`}>
                <img src={c.image} alt={c.theme} className="w-full h-full object-cover object-top" />
                <div className={`absolute inset-0 bg-gradient-to-t ${c.gradient}`} />
              </div>
            ))}
            <div className="absolute inset-0 flex flex-col justify-between p-4 z-10">
              <img src="https://public.readdy.ai/ai/img_res/87bc98b4-e616-459b-81b0-cc0db54579b8.png" alt="Logo" className="h-5 w-auto object-contain" />
              <div>
                <p className={`text-xs mb-1 ${card.textColor} opacity-80`}>XIAMEN · 鹭岛</p>
                <h4 className="text-white text-base font-semibold leading-tight mb-1.5" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                  遇见厦门<br />山海鹭岛
                </h4>
                <p className={`text-xs ${card.textColor} opacity-70`}>{card.tagline}</p>
                <div className="mt-2.5 w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                  <i className="ri-qr-code-line text-white text-base" />
                </div>
              </div>
            </div>
            <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent pointer-events-none" />
          </div>
          <div className="flex items-center gap-2">
            {shareCards.map((c, i) => (
              <button key={c.id} onClick={() => handleCardSelect(i)} className={`transition-all duration-300 rounded-full cursor-pointer ${i === activeCard ? "w-6 h-1.5 bg-white" : "w-1.5 h-1.5 bg-white/30 hover:bg-white/60"}`} />
            ))}
          </div>
          <p className="text-white/40 text-xs tracking-wider">{card.theme} · 点击切换</p>
        </div>
      </div>

      {/* Bottom: Chapter recap */}
      <div className={`relative z-20 px-5 md:px-16 pb-5 md:pb-8 transition-all duration-800 delay-400 ${entered ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
        <div className="flex items-center gap-1 mb-2">
          <div className="w-3 md:w-4 h-px bg-white/20" />
          <span className="text-white/30 text-xs tracking-widest">回顾旅程</span>
        </div>
        <div className="flex items-center gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: "none" }}>
          {[
            { icon: "ri-image-line", label: "城市封面" },
            { icon: "ri-sailboat-line", label: "山海之美" },
            { icon: "ri-ancient-pavilion-line", label: "人文鹭岛" },
            { icon: "ri-restaurant-line", label: "舌尖闽南" },
            { icon: "ri-building-2-line", label: "宜居宜业" },
            { icon: "ri-question-answer-line", label: "厦门印象" },
            { icon: "ri-star-line", label: "分享结尾" },
          ].map((ch, i) => (
            <div
              key={ch.label}
              className={`flex items-center gap-1.5 flex-shrink-0 px-2.5 md:px-3 py-1 md:py-1.5 rounded-full border transition-all duration-300 ${
                i === 6 ? "border-white/40 bg-white/10 text-white" : "border-white/10 bg-white/5 text-white/40"
              }`}
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className={`${ch.icon} text-xs`} />
              </div>
              <span className="text-xs whitespace-nowrap">{ch.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Back arrow */}
      <button
        onClick={onPrev}
        className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-30 w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 transition-all cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>

      {/* Page indicator */}
      <div className="absolute bottom-3 md:bottom-6 right-5 md:right-8 z-20 flex items-center gap-2">
        {[0, 1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className={`rounded-full transition-all duration-300 ${i === 6 ? "w-6 h-1.5 bg-amber-400" : "w-1.5 h-1.5 bg-white/20"}`} />
        ))}
      </div>

      {/* Toast */}
      <div className={`fixed bottom-14 md:bottom-16 left-1/2 -translate-x-1/2 z-50 transition-all duration-400 ${showToast ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3 pointer-events-none"}`}>
        <div className="flex items-center gap-2.5 bg-gray-900/90 backdrop-blur-md text-white text-xs md:text-sm px-4 md:px-5 py-2.5 md:py-3 rounded-full border border-white/10">
          <i className="ri-checkbox-circle-line text-green-400" />
          <span className="whitespace-nowrap">{toastMsg}</span>
        </div>
      </div>
    </div>
  );
}
